/**
 * AES 解密工具
 * 用于解密后端返回的加密数据
 * 使用 crypto-js 库实现 AES/CBC/PKCS5Padding 解密
 */

import CryptoJS from 'crypto-js';

/**
 * 密钥混淆：使用字符数组拆分 + 字符偏移
 * 混淆后的密钥（已拆分和偏移，每个字符 ASCII 码 +1）
 */
const OBFUSCATED_KEY_1_PARTS = ['y', 'N', ':', 'l', 'Q', '3', 'o', 'R', '9', 'w', 'S', '6', 'u', 'Z', '4', 'x'];
const OBFUSCATED_IV_1_PARTS = ['{', 'B', '8', 'c', 'D', '5', 'e', 'F', '7', 'g', 'H', '9', 'i', 'J', '2', 'k'];
const OBFUSCATED_KEY_2_PARTS = ['l', 'M', '3', 'n', 'O', '6', 'p', 'Q', '8', 'r', 'S', ':', 't', 'U', '2', 'v'];
const OBFUSCATED_IV_2_PARTS = ['w', 'X', '4', 'y', 'Z', '7', '{', 'B', '9', 'c', 'D', '2', 'e', 'F', '5', 'g'];

/**
 * 解码混淆的密钥
 * @param obfuscatedParts 混淆后的密钥字符数组
 * @returns 原始密钥字符串
 */
function deobfuscateKey(obfuscatedParts: string[]): string {
  try {
    // 字符偏移还原（每个字符 ASCII 码 - 1）
    return obfuscatedParts.map((char: string) => String.fromCharCode(char.charCodeAt(0) - 1)).join('');
  } catch (error) {
    throw new Error('密钥解码失败');
  }
}

// 小四门加密密钥常量（运行时解码）
const FOUR_GATES_SECRET_KEY = deobfuscateKey(OBFUSCATED_KEY_1_PARTS); // 16 bytes key for AES (小四门)
const FOUR_GATES_SECRET_IV = deobfuscateKey(OBFUSCATED_IV_1_PARTS);
const FOUR_GATES_SECRET_KEY_CONFIG = deobfuscateKey(OBFUSCATED_KEY_2_PARTS); // 16 bytes key for AES (小四门配置接口)
const FOUR_GATES_SECRET_IV_CONFIG = deobfuscateKey(OBFUSCATED_IV_2_PARTS);

/**
 * 判断是否为配置接口
 * @param url 请求 URL
 */
function isConfigUrl(url: string): boolean {
  return url.includes('/sduck/setting') || url.includes('/sduck/account/authRefresh');
}

/**
 * 根据 URL 获取对应的密钥和 IV
 * @param url 请求 URL
 */
function getKeyAndIv(url: string): { key: string; iv: string } {
  const isConfig = isConfigUrl(url);
  
  if (isConfig) {
    return {
      key: FOUR_GATES_SECRET_KEY_CONFIG,
      iv: FOUR_GATES_SECRET_IV_CONFIG,
    };
  }
  return {
    key: FOUR_GATES_SECRET_KEY,
    iv: FOUR_GATES_SECRET_IV,
  };
}

/**
 * AES 解密
 * @param encryptedData Base64 编码的加密数据
 * @param key 密钥（16 字节字符串）
 * @param iv 初始化向量（16 字节字符串）
 */
function aesDecrypt(encryptedData: string, key: string, iv: string): string {
  try {
    // Base64 解码
    const encrypted = CryptoJS.enc.Base64.parse(encryptedData);
    
    // 准备密钥和 IV
    const keyBytes = CryptoJS.enc.Utf8.parse(key);
    const ivBytes = CryptoJS.enc.Utf8.parse(iv);
    
    // AES/CBC/PKCS5Padding 解密
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted } as any,
      keyBytes,
      {
        iv: ivBytes,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
    
    // 转换为 UTF-8 字符串
    const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedStr) {
      throw new Error('解密结果为空，可能是密钥或数据错误');
    }
    
    return decryptedStr.trim();
  } catch (error: any) {
    throw new Error('AES 解密失败: ' + error.message);
  }
}

/**
 * 解密函数
 * @param encryptedData Base64 编码的加密数据
 * @param url 请求 URL（用于判断使用哪个密钥）
 */
export function decrypt(encryptedData: string, url: string = ''): string {
  try {
    const { key, iv } = getKeyAndIv(url);
    return aesDecrypt(encryptedData, key, iv);
  } catch (error: any) {
    throw error;
  }
}

/**
 * 创建解密函数（供 request.ts 使用）
 * 返回一个接受加密数据的解密函数
 */
export function createDecryptFunction(): (encryptedData: string, url?: string) => any {
  return (encryptedData: string, url: string = '') => {
    try {
      const decryptedStr = decrypt(encryptedData, url);
      // 尝试解析为 JSON
      try {
        return JSON.parse(decryptedStr);
      } catch {
        return decryptedStr;
      }
    } catch (error) {
      // 解密失败时返回原始数据
      console.error('[AES解密] 解密失败:', error);
      return encryptedData;
    }
  };
}

